import React from "react";

interface ErrorFallbackProps {
  resetError: () => void;
  eventId?: string;
}

/**
 * Fallback rendered by Sentry.ErrorBoundary when an uncaught render error occurs.
 * Styled to match the Healing Sky theme (primary navy #193b5a, Urbanist/Inter fonts).
 * Uses inline styles so it displays correctly even if the app stylesheet failed to load.
 * The error itself is already reported to Sentry by the ErrorBoundary.
 */
const PRIMARY = "#193b5a";
const PRIMARY_HOVER = "#0e2132";

const ErrorFallback: React.FC<ErrorFallbackProps> = ({ resetError, eventId }) => (
  <div
    style={{
      minHeight: "100vh",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      padding: "24px",
      backgroundColor: "#ffffff",
      fontFamily: "Inter, sans-serif",
    }}
  >
    <div style={{ maxWidth: "460px", textAlign: "center" }}>
      <div
        style={{
          width: "72px",
          height: "72px",
          margin: "0 auto 24px",
          borderRadius: "50%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: "#eef8f8",
          color: PRIMARY,
          fontSize: "34px",
          lineHeight: 1,
        }}
      >
        ⚠
      </div>
      <h2
        style={{
          fontFamily: "Urbanist, sans-serif",
          fontSize: "24px",
          fontWeight: 600,
          color: "#231f20",
          margin: "0 0 16px",
        }}
      >
        Something went wrong
      </h2>
      <p
        style={{
          fontFamily: "Inter, sans-serif",
          fontSize: "16px",
          fontWeight: 400,
          color: "#656263",
          margin: "0 0 24px",
          lineHeight: 1.6,
        }}
      >
        An unexpected error occurred and our team has been notified. You can try again or reload the
        page to continue.
      </p>
      <div style={{ display: "flex", gap: "12px", justifyContent: "center", flexWrap: "wrap" }}>
        <button
          type="button"
          onClick={resetError}
          style={{
            fontFamily: "Inter, sans-serif",
            fontSize: "16px",
            fontWeight: 600,
            color: PRIMARY,
            backgroundColor: "#ffffff",
            border: `1px solid ${PRIMARY}`,
            borderRadius: "8px",
            padding: "12px 24px",
            cursor: "pointer",
          }}
        >
          Try again
        </button>
        <button
          type="button"
          onClick={() => window.location.reload()}
          onMouseOver={(e) => (e.currentTarget.style.backgroundColor = PRIMARY_HOVER)}
          onMouseOut={(e) => (e.currentTarget.style.backgroundColor = PRIMARY)}
          style={{
            fontFamily: "Inter, sans-serif",
            fontSize: "16px",
            fontWeight: 600,
            color: "#ffffff",
            backgroundColor: PRIMARY,
            border: `1px solid ${PRIMARY}`,
            borderRadius: "8px",
            padding: "12px 24px",
            cursor: "pointer",
          }}
        >
          Reload page
        </button>
      </div>
      {eventId ? (
        <p
          style={{
            fontFamily: "Inter, sans-serif",
            fontSize: "13px",
            color: "#99a2a8",
            margin: "24px 0 0",
          }}
        >
          Reference ID: {eventId}
        </p>
      ) : null}
    </div>
  </div>
);

export default ErrorFallback;
